type Fn<T> = (...args: any[]) => Promise<T> | T;

interface CallOptions {
  /** 缓存时长（毫秒），不传或为 0 则不缓存 */
  ttl?: number;
}

/**
 * 创建一个带 single-flight 并发去重功能的函数
 * @param fn 原始异步函数
 * @param keyer 根据调用参数生成唯一 key（默认使用 fn 作为 key）
 * @param opts 配置，如 ttl
 */
export function createSingleFlight<T>(
  fn: Fn<T>,
  keyer?: (...args: any[]) => string,
  opts: CallOptions = {}
) {
  const inflight = new Map<any, Promise<T>>();
  const cache = new Map<any, { value: T; expiresAt: number | null }>();

  return async (...args: any[]): Promise<T> => {
    const key = keyer ? keyer(...args) : fn; // 如果没传 keyer，就用函数引用做 key

    // 命中有效缓存
    const cached = cache.get(key);
    if (
      cached &&
      (cached.expiresAt === null || cached.expiresAt > Date.now())
    ) {
      return cached.value;
    }

    // 有进行中的相同调用
    const running = inflight.get(key);
    if (running) return running;

    // 真正执行
    const fnExcuteRes = (async () => {
      try {
        const res = await fn(...args);
        if (opts.ttl && opts.ttl > 0) {
          cache.set(key, {
            value: res,
            expiresAt: Date.now() + opts.ttl,
          });
        }

        inflight.delete(key);
        return res;
      } finally {
        inflight.delete(key);
      }
    })();

    inflight.set(key, fnExcuteRes);
    return fnExcuteRes;
  };
}
